import { Alert, Box, ColumnLayout, FormField, Link, Select, SelectProps, SpaceBetween, Table, Toggle } from '@amzn/awsui-components-core';
import Input from '@amzn/awsui-components-core/input';
import { FormikProps, getIn } from 'formik';
import React, { useEffect, useRef } from 'react';
import { generateUrlName } from 'src/utilities/urlName';
import { useAppContext } from 'src/components/context/AppContextProvider';
import {
  labelInputToLabelDropdownOptions,
  labelInputToSelectDropdown,
  selectDropdownToLabel
} from 'src/components/generic-components/LabelDropdownUtility';
import { ApplicationFormValues } from '../../AdminModels';
import { NonProductionEnvironmentsConfig } from './LinksAndTags';

const QUICKSIGHT_CONSOLE_URL_PATTERN = /quicksight\.aws\.amazon\.com\/sn\/account\/[^/]+\/dashboards\//;
const QUICKSIGHT_EMBED_URL_PATTERN = /quicksight\.aws\.amazon\.com\/sn\/account\/[^/]+\/embed\/share\//;
const NON_EMBEDDABLE_WARNING = 'This looks like a QuickSight console URL, not an embed URL. Embedding may not work.';

export const isNonEmbeddableQuickSightUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return QUICKSIGHT_CONSOLE_URL_PATTERN.test(url) && !QUICKSIGHT_EMBED_URL_PATTERN.test(url);
};

interface ApplicationLinksConfigProps {
  formik: FormikProps<ApplicationFormValues>;
}

const ApplicationLinksConfig: React.FC<ApplicationLinksConfigProps> = ({ formik }) => {
  const appContext = useAppContext();

  const isQuickSight = formik.values.applicationDetails?.toolUI?.label === 'Quicksight';
  const isEmbeddingEnabled = formik.values.applicationDetails?.embeddingEnabled || false;

  // Auto-derive the App-specific Path from the application name ONLY when the app does not
  // already have a saved urlName, and only until the admin manually edits it.
  // An app that already has a urlName always displays its SAVED value — so
  // re-editing an app never silently swaps the saved slug for the auto-generated
  // one (which would dirty the form and risk an accidental change on save).
  // `initialValues` is read live in the effect because the app loads asynchronously
  // (Formik reinitializes once the real data arrives).
  const userEditedUrlName = useRef<boolean>(false);
  const applicationName = formik.values.applicationDetails?.applicationName;
  const savedUrlName = formik.initialValues.applicationDetails?.urlName;
  useEffect(() => {
    if (savedUrlName || userEditedUrlName.current) return;
    const derived = generateUrlName(applicationName);
    if ((formik.values.applicationDetails?.urlName || '') !== derived) {
      formik.setFieldValue('applicationDetails.urlName', derived);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationName, savedUrlName]);

  return (
    <SpaceBetween size="m" direction="vertical">
      <ColumnLayout columns={2}>
        <FormField
          label="Product UI"
          description="Select the product UI framework for this app"
          errorText={getIn(formik.touched, 'applicationDetails.toolUI') && getIn(formik.errors, 'applicationDetails.toolUI.label')}
        >
          <Select
            placeholder="Choose product UI"
            selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.toolUI)}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.toolUI', selectDropdownToLabel(detail.selectedOption, 'ToolUI'));
            }}
            onBlur={() => {
              formik.setFieldTouched('applicationDetails.toolUI', true);
            }}
            options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listToolUI)}
            filteringType="auto"
            selectedAriaLabel="Selected"
          />
        </FormField>

        <FormField label="Embedding Enabled" description="Allow this app to be embedded within FinSuite">
          <Toggle
            checked={formik.values.applicationDetails?.embeddingEnabled || false}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.embeddingEnabled', detail.checked);
            }}
          >
            {formik.values.applicationDetails?.embeddingEnabled ? 'Enabled' : 'Disabled'}
          </Toggle>
        </FormField>
      </ColumnLayout>

      <FormField
        label="App-specific Path"
        description="Human-readable path for the app in FinSuite (e.g. finsuite.device.finance.amazon.dev/app/yeti-bom)"
        constraintText='Lowercase letters and numbers - no spaces or special characters (except "-" or "_")'
        errorText={getIn(formik.touched, 'applicationDetails.urlName') && getIn(formik.errors, 'applicationDetails.urlName')}
        stretch={true}
      >
        <Input
          placeholder="e.g. prophecy-arc"
          value={formik.values.applicationDetails?.urlName || ''}
          onChange={({ detail }) => {
            userEditedUrlName.current = true;
            formik.setFieldValue('applicationDetails.urlName', detail.value);
            // Mark touched on change (not just blur) so an invalid value shows its
            // error immediately and stays visible — prevents the brief error "flash".
            formik.setFieldTouched('applicationDetails.urlName', true, false);
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.urlName', true);
          }}
        />
      </FormField>

      {isEmbeddingEnabled && isQuickSight && (
        <Alert type="info" header="QuickSight Embedding Setup">
          <SpaceBetween size="s">
            <Box>
              To embed a QuickSight dashboard, use the <strong>shareable embed link</strong> (not the browser URL). In QuickSight, open the dashboard,
              click <strong>Share &rarr; Copy embed link</strong>, and paste it below.
            </Box>
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'type', header: 'Type', width: 120, cell: (item) => <strong>{item.type}</strong> },
                {
                  id: 'pattern',
                  header: 'URL Pattern',
                  cell: (item) => <code>{item.pattern}</code>
                },
                {
                  id: 'embeddable',
                  header: 'Embeddable?',
                  width: 120,
                  cell: (item) => <Box color={item.embeddable ? 'text-status-success' : 'text-status-error'}>{item.embeddable ? 'Yes' : 'No'}</Box>
                }
              ]}
              items={[
                { type: 'Console URL', pattern: '/sn/account/{alias}/dashboards/{id}', embeddable: false },
                { type: 'Embed URL', pattern: '/sn/account/{alias}/embed/share/accounts/{accountId}/dashboards/{id}', embeddable: true }
              ]}
            />
            <Box>
              The QuickSight administrator must also allowlist FinSuite's domain (<code>finsuite.device.finance.amazon.dev</code>) under{' '}
              <strong>Manage QuickSight &rarr; Domains and Embedding</strong>.{' '}
              <Link external href="https://docs.aws.amazon.com/quicksight/latest/user/embedding-with-iframe.html">
                Learn more
              </Link>
            </Box>
          </SpaceBetween>
        </Alert>
      )}

      <FormField
        label={
          <>
            Production Link<i> - optional</i>
          </>
        }
        description="The production URL used when embedding or linking to this application only in FinSuite Prod"
        constraintText="Include http:// or https:// in the URL"
        errorText={getIn(formik.touched, 'applicationDetails.applicationLink') && getIn(formik.errors, 'applicationDetails.applicationLink')}
        warningText={
          isQuickSight && isEmbeddingEnabled && isNonEmbeddableQuickSightUrl(formik.values.applicationDetails?.applicationLink)
            ? NON_EMBEDDABLE_WARNING
            : undefined
        }
        stretch={true}
      >
        <Input
          placeholder="Paste production URL"
          onChange={({ detail }) => {
            formik.setFieldValue('applicationDetails.applicationLink', detail.value);
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.applicationLink', true);
          }}
          value={formik.values.applicationDetails?.applicationLink || ''}
        />
      </FormField>

      <NonProductionEnvironmentsConfig formik={formik} showEmbedWarnings={isQuickSight && isEmbeddingEnabled} />
    </SpaceBetween>
  );
};

export default ApplicationLinksConfig;
